import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  SettingEditor,
  EpisodeOutlineList,
  SubjectSettingList,
  EpisodeOutlineForm,
  FailureRecoveryModal,
} from '../components/projectSettings';
import { ProjectLayout, type NavItem } from '../components/projects';
import { ScriptEditor, ChapterForm, ScriptChatPanel, CreateEpisodeDialog } from '../components/projectScripts';
import type { ScriptChatPanelRef, CreateEpisodeConfig } from '../components/projectScripts';
import { EpisodeGrid, EpisodeFormDrawer } from '../components/episodes';
import type { EpisodeFormData } from '../components/episodes';
import CreateScriptSection from '../components/ScriptGeneration/CreateScriptSection';
import Pagination from '../components/Pagination';
import { useAssets } from '../hooks/useAssets';
import { useAutoGeneration } from '../hooks/useAutoGeneration';
import { useEpisodesList } from '../hooks/useEpisodes';
import { useDialog } from '../hooks/useDialog';
import { projectSettingApi } from '../services/user/projectSetting.api';
import { episodeOutlineApi } from '../services/user/episodeOutline.api';
import { projectScriptApi } from '../services/user/projectScript.api';
import { episodeScriptApi } from '../services/user/episodeScript.api';
import { episodeApi } from '../services/user/episode.api';
import { useToast } from '../stores/toastStore';
import { useProjectStore } from '../stores/projectStore';
import { useGlobalLoading } from '../stores/globalLoadingStore';
import {
  ProjectSettingType,
  IProjectSetting,
  IEpisodeOutline,
  IProjectScript,
  IEpisode,
  CreateEpisodeOutlineRequest,
  CreateProjectScriptRequest,
  UpdateProjectScriptRequest,
  TOTAL_GENERATION_STEPS,
} from '@packages/global';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

// ── 类型 ──────────────────────────────────────────────────────────────────────
type TabKey = 'settings' | 'script' | 'episodes';

// ── URL 解析辅助 ──────────────────────────────────────────────────────────────
function parseTabFromUrl(searchParams: URLSearchParams): TabKey {
  const tab = searchParams.get('tab');
  if (tab === 'script' || tab === 'episodes') return tab;
  return 'settings';
}

function parseNavFromUrl(searchParams: URLSearchParams): NavItem {
  const nav = searchParams.get('nav');
  const tab = searchParams.get('tab');

  if (tab === 'script') return { type: 'script' };
  if (tab === 'episodes') return { type: 'episodes' };

  if (!nav) return { type: 'setting', settingType: ProjectSettingType.STORY_SYNOPSIS };
  if (Object.values(ProjectSettingType).includes(nav as ProjectSettingType)) {
    return { type: 'setting', settingType: nav as ProjectSettingType };
  }
  if (nav === 'outline') return { type: 'outline' };
  if (nav === 'subject') return { type: 'subject' };
  if (nav === 'script') return { type: 'script' };
  if (nav === 'episodes') return { type: 'episodes' };

  return { type: 'setting', settingType: ProjectSettingType.STORY_SYNOPSIS };
}

// ── 主组件 ────────────────────────────────────────────────────────────────────
export default function ProjectPage() {
  const { t } = useTranslation(['project', 'common']);
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm } = useDialog();
  const { showLoading, hideLoading } = useGlobalLoading();
  const { loadProject } = useProjectStore();

  // ── 当前 tab & nav ──────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<TabKey>(() => parseTabFromUrl(searchParams));
  const [activeNavItem, setActiveNavItem] = useState<NavItem>(() => parseNavFromUrl(searchParams));
  const [mountedTabs, setMountedTabs] = useState<Set<TabKey>>(() => new Set([parseTabFromUrl(searchParams)]));

  // ── Settings 数据 ───────────────────────────────────────────────────────────
  const [settings, setSettings] = useState<Record<ProjectSettingType, IProjectSetting | null>>({
    [ProjectSettingType.WORLDVIEW]: null,
    [ProjectSettingType.WRITING_STYLE]: null,
    [ProjectSettingType.ART_STYLE]: null,
    [ProjectSettingType.STORY_SYNOPSIS]: null,
  });
  const [outlines, setOutlines] = useState<IEpisodeOutline[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [showOutlineForm, setShowOutlineForm] = useState(false);
  const [editingOutline, setEditingOutline] = useState<IEpisodeOutline | null>(null);
  const [showFailureModal, setShowFailureModal] = useState(false);

  const { assets: subjects, loading: assetsLoading, loadAssets: loadSubjects } = useAssets({ projectId: projectId! });

  // ── Script 数据 ─────────────────────────────────────────────────────────────
  const [scripts, setScripts] = useState<IProjectScript[]>([]);
  const [scriptsLoading, setScriptsLoading] = useState(false);
  const [showChapterForm, setShowChapterForm] = useState(false);
  const [editingChapter, setEditingChapter] = useState<IProjectScript | null>(null);
  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(null);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [showCreateEpisodeDialog, setShowCreateEpisodeDialog] = useState(false);
  const [isCreatingEpisode, setIsCreatingEpisode] = useState(false);
  const chatPanelRef = useRef<ScriptChatPanelRef>(null);
  const hasTriggeredGenerate = useRef(false);

  // ── 自动生成 ────────────────────────────────────────────────────────────────
  const {
    state: autoGenState,
    isLoading: autoGenLoading,
    retry,
    cancel,
    switchToManualMode,
  } = useAutoGeneration(projectId, {
    onProjectMetaUpdated: () => { if (projectId) loadProject(projectId); },
    onStepCompleted: (stepType) => {
      const settingSteps = ['story_synopsis', 'worldview', 'writing_style', 'art_style', 'project_meta', 'novel_parse'];
      if (settingSteps.includes(stepType)) loadSettings();
      if (stepType === 'episode_outline') loadOutlines();
      if (stepType === 'subject') loadSubjects();
      if (stepType === 'episode_script') loadScripts();
      if ((stepType === 'episode_script' || stepType === 'project_meta') && projectId) loadProject(projectId);
    },
  });

  useEffect(() => {
    if (autoGenState.failedStep && autoGenState.errorMessage) setShowFailureModal(true);
  }, [autoGenState.failedStep, autoGenState.errorMessage]);

  // ── Episodes 数据 ───────────────────────────────────────────────────────────
  const {
    episodes,
    loading: episodesLoading,
    showForm: showEpisodeCreateForm,
    searchTerm,
    pagination,
    setShowForm: setShowEpisodeCreateForm,
    handleDelete: handleDeleteEpisode,
    loadEpisodes,
  } = useEpisodesList(projectId!);

  const [showEditEpisodeForm, setShowEditEpisodeForm] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState<IEpisode | null>(null);

  // ── 初始加载 ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    loadSettings();
    loadOutlines();
    loadScripts();
  }, [projectId]);

  // ── URL 参数处理（仅首次或外部跳转时） ──────────────────────────────────────
  const pendingScriptParamsRef = useRef<{ chapterId: string; generate: boolean; outlineTitle: string | null } | null>(null);
  const urlParamsProcessed = useRef(false);

  useEffect(() => {
    if (urlParamsProcessed.current) return;
    urlParamsProcessed.current = true;

    const chapterId = searchParams.get('chapterId');
    const generate = searchParams.get('generate') === 'true';
    const outlineTitle = searchParams.get('outlineTitle');

    if (chapterId) {
      pendingScriptParamsRef.current = { chapterId, generate, outlineTitle };
    }

    // 清除 URL 参数
    if (searchParams.toString()) {
      setSearchParams({}, { replace: true });
    }
  }, []);

  // 当 scripts 加载完成后，处理待触发的自动生成
  useEffect(() => {
    const params = pendingScriptParamsRef.current;
    if (!params || scripts.length === 0 || hasTriggeredGenerate.current) return;

    const chapter = scripts.find(s => s.id === params.chapterId);
    if (!chapter) return;

    setSelectedChapterId(params.chapterId);
    setShowChatPanel(true);
    switchTab('script');

    if (params.generate) {
      hasTriggeredGenerate.current = true;
      pendingScriptParamsRef.current = null;
      setTimeout(() => {
        const prompt = params.outlineTitle
          ? i18n.t('project:aiPrompt.generateFromOutline', { outlineTitle: params.outlineTitle, chapterTitle: chapter.title })
          : i18n.t('project:aiPrompt.generateForChapter', { chapterTitle: chapter.title });
        chatPanelRef.current?.triggerGenerate(prompt);
      }, 500);
    }
  }, [scripts]);

  // ── Settings 操作 ────────────────────────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    if (!projectId) return;
    try {
      setSettingsLoading(true);
      const list = await projectSettingApi.getSettings(projectId);
      const map: Record<ProjectSettingType, IProjectSetting | null> = {
        [ProjectSettingType.WORLDVIEW]: null,
        [ProjectSettingType.WRITING_STYLE]: null,
        [ProjectSettingType.ART_STYLE]: null,
        [ProjectSettingType.STORY_SYNOPSIS]: null,
      };
      list.forEach(s => { map[s.settingType] = s; });
      setSettings(map);
    } catch (e) {
      console.error('Failed to load project settings:', e);
    } finally {
      setSettingsLoading(false);
    }
  }, [projectId]);

  const loadOutlines = useCallback(async () => {
    if (!projectId) return;
    try {
      setOutlines(await episodeOutlineApi.list(projectId));
    } catch (e) {
      console.error('Failed to load episode outlines:', e);
    }
  }, [projectId]);

  const handleSaveSetting = async (content: string) => {
    if (!projectId || activeNavItem.type !== 'setting') return;
    const settingType = activeNavItem.settingType!;
    const updated = await projectSettingApi.updateSetting(projectId, settingType, { content });
    setSettings(prev => ({ ...prev, [settingType]: updated }));
    toast.success(t('project:toast.settingSaved'));
  };

  const handleSubmitOutline = async (data: CreateEpisodeOutlineRequest) => {
    if (editingOutline) {
      await episodeOutlineApi.update(editingOutline.id, { episodeNumber: data.episodeNumber, title: data.title, synopsis: data.synopsis });
      toast.success(t('project:toast.outlineUpdated'));
    } else {
      await episodeOutlineApi.create(data);
      toast.success(t('project:toast.outlineCreated'));
    }
    setShowOutlineForm(false);
    await loadOutlines();
  };

  const handleDeleteOutline = async (outline: IEpisodeOutline) => {
    const ok = await confirm(t('project:outline.deleteConfirm', { title: outline.title }), t('app:dialog.confirmDelete'), 'danger');
    if (!ok) return;
    await episodeOutlineApi.delete(outline.id);
    await loadOutlines();
    toast.success(t('project:toast.outlineDeleted'));
  };

  const handleGenerateScript = async (outline: IEpisodeOutline) => {
    if (!projectId) return;
    if (outline.generatedScriptId) {
      setSelectedChapterId(outline.generatedScriptId);
      setShowChatPanel(true);
      switchTab('script');
      return;
    }
    const existingScripts = await projectScriptApi.list(projectId);
    const nextNum = existingScripts.length > 0 ? Math.max(...existingScripts.map(s => s.chapterNumber)) + 1 : 1;
    const newChapter = await projectScriptApi.create({ projectId, chapterNumber: nextNum, title: outline.title, content: '' });
    await episodeOutlineApi.update(outline.id, { generatedScriptId: newChapter.id });
    await loadOutlines();
    setScripts(prev => [...prev, newChapter]);
    setSelectedChapterId(newChapter.id);
    setShowChatPanel(true);
    switchTab('script');
    toast.success(t('project:toast.chapterCreated'));
    setTimeout(() => {
      const prompt = i18n.t('project:aiPrompt.generateFromOutline', { outlineTitle: outline.title, chapterTitle: newChapter.title });
      chatPanelRef.current?.triggerGenerate(prompt);
    }, 500);
  };

  // ── Script 操作 ──────────────────────────────────────────────────────────────
  const loadScripts = useCallback(async () => {
    if (!projectId) return;
    try {
      setScriptsLoading(true);
      setScripts(await projectScriptApi.list(projectId));
    } catch (e) {
      console.error('Failed to load script chapters:', e);
    } finally {
      setScriptsLoading(false);
    }
  }, [projectId]);

  const handleSaveChapter = async (chapter: IProjectScript) => {
    const data: UpdateProjectScriptRequest = { title: chapter.title, content: chapter.content };
    await projectScriptApi.update(chapter.id, data);
    setScripts(prev => prev.map(s => s.id === chapter.id ? { ...s, ...data } : s));
  };

  const handleSubmitChapter = async (title: string) => {
    if (!projectId) return;
    if (editingChapter) {
      await projectScriptApi.update(editingChapter.id, { title });
      setScripts(prev => prev.map(s => s.id === editingChapter.id ? { ...s, title } : s));
      toast.success(t('project:toast.chapterUpdated'));
    } else {
      const nextNum = scripts.length > 0 ? Math.max(...scripts.map(s => s.chapterNumber)) + 1 : 1;
      const data: CreateProjectScriptRequest = { projectId, chapterNumber: nextNum, title, content: '' };
      const newChapter = await projectScriptApi.create(data);
      setScripts(prev => [...prev, newChapter]);
      setSelectedChapterId(newChapter.id);
      toast.success(t('project:toast.chapterCreated'));
    }
    setShowChapterForm(false);
  };

  const handleDeleteChapter = async (chapter: IProjectScript) => {
    if (!confirm(t('project:chapter.deleteConfirm', { title: chapter.title }))) return;
    await projectScriptApi.delete(chapter.id);
    setScripts(prev => prev.filter(s => s.id !== chapter.id));
    if (selectedChapterId === chapter.id) { setSelectedChapterId(null); setShowChatPanel(false); }
    toast.success(t('project:toast.chapterDeleted'));
  };

  const handleReorderChapters = async (reordered: IProjectScript[]) => {
    if (!projectId) return;
    setScripts(reordered);
    try {
      await projectScriptApi.reorder({ projectId, orders: reordered.map(ch => ({ id: ch.id, sortOrder: ch.sortOrder || ch.chapterNumber, chapterNumber: ch.chapterNumber })) });
    } catch {
      toast.error(t('project:chapter.reorder'));
      loadScripts();
    }
  };

  const handleApplyContent = (content: string, title?: string) => {
    if (!selectedChapterId) return;
    const chapter = scripts.find(s => s.id === selectedChapterId);
    if (!chapter) return;
    const updated = { ...chapter, content, ...(title && { title }) };
    setScripts(prev => prev.map(s => s.id === selectedChapterId ? updated : s));
    handleSaveChapter(updated);
  };

  const handleCreateEpisode = async (config: CreateEpisodeConfig) => {
    if (!selectedChapterId || !projectId) return;
    try {
      setIsCreatingEpisode(true);
      setShowCreateEpisodeDialog(false);
      showLoading(t('project:toast.episodeCreating'));
      const result = await episodeScriptApi.createEpisode({
        prompt: config.prompt,
        projectId,
        projectScriptId: selectedChapterId,
        imageModelId: config.text2imageModelId,
        llmModelId: config.llmModelId,
        aspectRatio: config.aspectRatio,
        targetDuration: config.targetDuration ?? undefined,
        maxShotDuration: config.maxShotDuration ?? undefined,
      });
      toast.success(t('project:toast.episodeCreated'));
      navigate(`/episode/${result.episodeId}/script-generation`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || t('project:toast.episodeCreateFailed'));
    } finally {
      setIsCreatingEpisode(false);
      hideLoading();
    }
  };

  // ── Episodes 操作 ────────────────────────────────────────────────────────────
  const handleSubmitEpisode = async (data: EpisodeFormData) => {
    if (!editingEpisode) return;
    await episodeApi.updateEpisode(editingEpisode.id, { title: data.title, description: data.description, thumbnailUrl: data.thumbnailUrl || undefined });
    await loadEpisodes({ projectId: projectId!, search: searchTerm }, pagination.page, pagination.limit);
    toast.success(t('project:toast.episodeUpdated'));
  };

  // ── Tab 切换 ─────────────────────────────────────────────────────────────────
  const switchTab = useCallback((tab: TabKey, nav?: NavItem) => {
    setActiveTab(tab);
    if (nav) setActiveNavItem(nav);
    else if (tab === 'script') setActiveNavItem({ type: 'script' });
    else if (tab === 'episodes') setActiveNavItem({ type: 'episodes' });
    setMountedTabs(prev => prev.has(tab) ? prev : new Set([...prev, tab]));
  }, []);

  const handleNavItemChange = useCallback((item: NavItem) => {
    if (item.type === 'script') {
      switchTab('script');
    } else if (item.type === 'episodes') {
      switchTab('episodes');
    } else {
      switchTab('settings', item);
    }
  }, [switchTab]);

  // ── 自动生成辅助 ─────────────────────────────────────────────────────────────
  const handleRetry = async () => {
    if (!autoGenState.failedStep) return;
    await retry(autoGenState.failedStep);
    setShowFailureModal(false);
    toast.info(t('project:toast.retrying'));
  };

  const handleSwitchToManual = async () => {
    await switchToManualMode();
    setShowFailureModal(false);
    toast.success(t('project:toast.switchedToManual'));
  };

  // ── 派生值 ───────────────────────────────────────────────────────────────────
  const activeSettingType = activeNavItem.type === 'setting' ? activeNavItem.settingType! : ProjectSettingType.STORY_SYNOPSIS;
  const projectArtStyle = useMemo(() => settings[ProjectSettingType.ART_STYLE]?.content || '', [settings[ProjectSettingType.ART_STYLE]?.content]);
  const selectedChapter = scripts.find(s => s.id === selectedChapterId);
  const autoGenStatus = (autoGenState.isActive || autoGenState.completedSteps.length > 0) ? {
    isActive: autoGenState.isActive,
    currentStep: autoGenState.currentStep,
    completedSteps: autoGenState.completedSteps,
    failedStep: autoGenState.failedStep,
    totalSteps: TOTAL_GENERATION_STEPS,
    novelFileUrl: autoGenState.novelFileUrl,
  } : undefined;

  return (
    <ProjectLayout
      activeTab={activeTab === 'settings' ? 'settings' : activeTab}
      activeNavItem={activeNavItem}
      onNavItemChange={handleNavItemChange}
      autoGenStatus={autoGenStatus}
    >
      {/* ── Settings Tab ─────────────────────────────────────────────────────── */}
      {mountedTabs.has('settings') && (
        <section className={`flex-1 p-6 overflow-auto ${activeTab === 'settings' ? '' : 'hidden'}`}>
          {activeNavItem.type === 'setting' && (
            <SettingEditor
              projectId={projectId!}
              settingType={activeSettingType}
              setting={settings[activeSettingType]}
              loading={settingsLoading}
              onSave={handleSaveSetting}
            />
          )}
          {activeNavItem.type === 'outline' && (
            <EpisodeOutlineList
              projectId={projectId!}
              outlines={outlines}
              loading={settingsLoading}
              onAdd={() => { setEditingOutline(null); setShowOutlineForm(true); }}
              onEdit={(o) => { setEditingOutline(o); setShowOutlineForm(true); }}
              onDelete={handleDeleteOutline}
              onGenerateScript={handleGenerateScript}
              onRefresh={loadOutlines}
            />
          )}
          {activeNavItem.type === 'subject' && (
            <SubjectSettingList
              projectId={projectId!}
              subjects={subjects}
              loading={assetsLoading}
              onRefresh={loadSubjects}
              projectArtStyle={projectArtStyle}
            />
          )}
        </section>
      )}

      {/* ── Script Tab ───────────────────────────────────────────────────────── */}
      {mountedTabs.has('script') && (
        <div className={`flex-1 flex overflow-hidden min-h-0 ${activeTab === 'script' ? '' : 'hidden'}`}>
          <section className={`flex-1 flex flex-col p-6 min-h-0 overflow-hidden transition-all ${showChatPanel ? 'pr-3' : ''}`}>
            <ScriptEditor
              projectId={projectId!}
              chapters={scripts}
              loading={scriptsLoading}
              selectedId={selectedChapterId}
              onAddChapter={() => { setEditingChapter(null); setShowChapterForm(true); }}
              onEditChapter={(c) => { setEditingChapter(c); setShowChapterForm(true); }}
              onSelectChapter={(c) => { setSelectedChapterId(c.id); setShowChatPanel(true); }}
              onSaveChapter={handleSaveChapter}
              onDeleteChapter={handleDeleteChapter}
              onReorderChapters={handleReorderChapters}
              onCreateEpisode={(c) => { if (c?.id) setSelectedChapterId(c.id); setShowCreateEpisodeDialog(true); }}
              isCreatingEpisode={isCreatingEpisode}
            />
          </section>
          {showChatPanel && selectedChapter && (
            <aside className="w-[380px] p-4 pl-3 flex-shrink-0 min-h-0 overflow-hidden flex flex-col">
              <ScriptChatPanel
                ref={chatPanelRef}
                projectId={projectId!}
                chapterId={selectedChapter.id}
                chapterTitle={selectedChapter.title}
                onApplyContent={handleApplyContent}
              />
            </aside>
          )}
        </div>
      )}

      {/* ── Episodes Tab ─────────────────────────────────────────────────────── */}
      {mountedTabs.has('episodes') && (
        <div className={`flex-1 overflow-auto p-6 ${activeTab === 'episodes' ? '' : 'hidden'}`}>
          <EpisodeGrid
            episodes={episodes}
            loading={episodesLoading}
            onCreateEpisode={() => setShowEpisodeCreateForm(true)}
            onEditEpisode={(ep) => { setEditingEpisode(ep); setShowEditEpisodeForm(true); }}
            onDeleteEpisode={handleDeleteEpisode}
          />
          {pagination.totalPages > 1 && (
            <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-4 mt-6">
              <Pagination
                page={pagination.page}
                limit={pagination.limit}
                total={pagination.total}
                totalPages={pagination.totalPages}
                onPageChange={(p) => loadEpisodes({ projectId: projectId!, search: searchTerm }, p, pagination.limit)}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Modals ───────────────────────────────────────────────────────────── */}
      <EpisodeOutlineForm
        isOpen={showOutlineForm}
        onClose={() => setShowOutlineForm(false)}
        onSubmit={handleSubmitOutline}
        projectId={projectId!}
        editingOutline={editingOutline}
        nextEpisodeNumber={outlines.length + 1}
      />

      <ChapterForm
        isOpen={showChapterForm}
        onClose={() => setShowChapterForm(false)}
        onSubmit={handleSubmitChapter}
        editingChapter={editingChapter}
      />

      {selectedChapter && (
        <CreateEpisodeDialog
          isOpen={showCreateEpisodeDialog}
          onClose={() => setShowCreateEpisodeDialog(false)}
          onConfirm={handleCreateEpisode}
          isProcessing={isCreatingEpisode}
          chapterTitle={selectedChapter.title}
        />
      )}

      {showEpisodeCreateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-4xl bg-bg-surface rounded-2xl shadow-2xl border border-border-subtle overflow-visible">
            <button
              onClick={() => setShowEpisodeCreateForm(false)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-50 hover:bg-bg-elevated rounded-full transition-all z-10 cursor-pointer"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            <div className="p-2 overflow-visible">
              <CreateScriptSection projectId={projectId} />
            </div>
          </div>
        </div>
      )}

      <EpisodeFormDrawer
        isOpen={showEditEpisodeForm}
        editingEpisode={editingEpisode}
        onSubmit={handleSubmitEpisode}
        onClose={() => { setShowEditEpisodeForm(false); setEditingEpisode(null); }}
      />

      {autoGenState.failedStep && (
        <FailureRecoveryModal
          isOpen={showFailureModal}
          onClose={() => setShowFailureModal(false)}
          failedStep={autoGenState.failedStep}
          errorMessage={autoGenState.errorMessage || t('project:unknownError')}
          onRetry={handleRetry}
          onSwitchToManual={handleSwitchToManual}
          isLoading={autoGenLoading}
        />
      )}
    </ProjectLayout>
  );
}
